import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Upload, CheckCircle, XCircle, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { Modal } from '../../_shared/components';
import { parseCsv } from '../utils/parse-csv';
import { addCampaignMember } from '../event-manager-client';
import { isRateLimited } from '../utils/rate-limit';

type ApiFetch = (input: RequestInfo | URL, init?: RequestInit & { timeoutMs?: number }) => Promise<Response>;
type Step = 1 | 2 | 3 | 4;

interface Props {
  open: boolean;
  onClose: () => void;
  campaignId: string;
  apiFetch: ApiFetch;
  cmStatuses: string[];
  onRefreshStatuses: () => void;
  onComplete: () => void;
}

interface ParsedData {
  headers: string[];
  rows: string[][];
}

interface ColumnMapping {
  mode: 'full' | 'split';
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
  notes: string;
}

type RowStatus = 'valid' | 'warning' | 'error';

interface ValidatedRow {
  name: string;
  email: string;
  status: string;
  notes: string;
  rowStatus: RowStatus;
  errorReason?: string;
  originalIndex: number;
}

interface UploadResult {
  index: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

const HEADER_MATCHERS: Record<string, (h: string) => boolean> = {
  fullName: (h) => ['full name', 'name', 'full_name', 'fullname', 'contact name', 'member name'].includes(h),
  firstName: (h) => ['first name', 'first_name', 'firstname', 'first'].includes(h),
  lastName: (h) => ['last name', 'last_name', 'lastname', 'last'].includes(h),
  email: (h) => ['email', 'work email', 'work_email', 'workemail', 'email address', 'e-mail'].includes(h),
  status: (h) => ['status', 'cm_status', 'campaign_member_status', 'member status'].includes(h),
  notes: (h) => ['notes', 'intel_notes', 'pre-event notes', 'pre_event_notes', 'comments'].includes(h) || h.includes('notes') || h.includes('follow up') || h.includes('follow-up'),
};

function autoDetectMapping(headers: string[]): ColumnMapping {
  const lower = headers.map(h => h.toLowerCase().trim());
  const find = (matcher: (h: string) => boolean) => {
    const idx = lower.findIndex(matcher);
    return idx >= 0 ? headers[idx] : '';
  };

  const fullName = find(HEADER_MATCHERS.fullName);
  const firstName = find(HEADER_MATCHERS.firstName);
  const lastName = find(HEADER_MATCHERS.lastName);
  const email = find(HEADER_MATCHERS.email);
  const status = find(HEADER_MATCHERS.status);
  const notes = find(HEADER_MATCHERS.notes);

  const hasFullName = !!fullName;
  const hasSplitName = !!firstName || !!lastName;

  return {
    mode: hasFullName ? 'full' : hasSplitName ? 'split' : 'full',
    fullName,
    firstName,
    lastName,
    email,
    status,
    notes,
  };
}

const THROTTLE_MS = 1000;
const RETRY_PAUSE_MS = 5000;

function delay(ms: number) { return new Promise(r => setTimeout(r, ms)); }

// Shared input class
const inputCls = 'w-full text-sm bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded-lg px-3 py-2 text-nightshade-700 dark:text-nightshade-300 focus:outline-none focus:ring-2 focus:ring-blueberry-400';
const labelCls = 'block text-xs font-medium text-nightshade-600 dark:text-nightshade-300 mb-1';
const btnPrimary = 'px-4 py-1.5 text-sm rounded-full bg-tangerine-500 text-white hover:bg-tangerine-600 transition-colors disabled:opacity-50';
const btnSecondary = 'px-4 py-1.5 text-sm rounded-full border border-nightshade-200 dark:border-nightshade-600 text-nightshade-600 dark:text-nightshade-300 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 transition-colors';

export function BulkUploadModal({ open, onClose, campaignId, apiFetch, cmStatuses, onRefreshStatuses, onComplete }: Props) {
  const [step, setStep] = useState<Step>(1);
  const [parsed, setParsed] = useState<ParsedData | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({ mode: 'full', fullName: '', firstName: '', lastName: '', email: '', status: '', notes: '' });
  const [defaultStatus, setDefaultStatus] = useState('Invited');
  const [fileError, setFileError] = useState('');
  const [validatedRows, setValidatedRows] = useState<ValidatedRow[]>([]);
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);
  const [uploading, setUploading] = useState(false);
  const cancelRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    cancelRef.current = false;
    setStep(1);
    setParsed(null);
    setMapping({ mode: 'full', fullName: '', firstName: '', lastName: '', email: '', status: '', notes: '' });
    setDefaultStatus('Invited');
    setFileError('');
    setValidatedRows([]);
    setUploadResults([]);
    setUploading(false);
  }, []);

  const handleClose = useCallback(() => {
    if (uploading) return; // Block close during upload
    reset();
    onClose();
  }, [uploading, reset, onClose]);

  const handleRequestClose = useCallback(() => {
    if (uploading) return false;
    reset();
    return true;
  }, [uploading, reset]);

  // ── Step 1: File parsing ──────────────────────────────────────────────
  const handleFile = useCallback((file: File) => {
    setFileError('');
    if (!file.name.endsWith('.csv')) {
      setFileError('Please select a .csv file');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      try {
        const result = parseCsv(text);
        if (result.headers.length === 0) {
          setFileError('No headers found in CSV');
          return;
        }
        if (result.rows.length === 0) {
          setFileError('No data rows found in CSV');
          return;
        }
        setParsed(result);
        const autoMapping = autoDetectMapping(result.headers);
        setMapping(autoMapping);
        setStep(2);
      } catch {
        setFileError('Failed to parse CSV file');
      }
    };
    reader.onerror = () => setFileError('Failed to read file');
    reader.readAsText(file);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  // ── Step 2 → 3: Validate rows ────────────────────────────────────────
  const canProceedToStep3 = mapping.email && (mapping.mode === 'full' ? mapping.fullName : (mapping.firstName || mapping.lastName));

  const buildValidatedRows = useCallback(() => {
    if (!parsed) return;
    const headerIdx = (col: string) => parsed.headers.indexOf(col);

    const rows: ValidatedRow[] = parsed.rows.map((row, i) => {
      let name = '';
      if (mapping.mode === 'full') {
        const idx = headerIdx(mapping.fullName);
        name = idx >= 0 ? (row[idx] || '').trim() : '';
      } else {
        const fi = headerIdx(mapping.firstName);
        const li = headerIdx(mapping.lastName);
        const first = fi >= 0 ? (row[fi] || '').trim() : '';
        const last = li >= 0 ? (row[li] || '').trim() : '';
        name = [first, last].filter(Boolean).join(' ');
      }

      const emailIdx = headerIdx(mapping.email);
      const email = emailIdx >= 0 ? (row[emailIdx] || '').trim() : '';

      let status = defaultStatus;
      if (mapping.status) {
        const statusIdx = headerIdx(mapping.status);
        const raw = statusIdx >= 0 ? (row[statusIdx] || '').trim() : '';
        if (raw) status = raw;
      }

      let notes = '';
      if (mapping.notes) {
        const notesIdx = headerIdx(mapping.notes);
        notes = notesIdx >= 0 ? (row[notesIdx] || '').trim() : '';
      }

      // Determine row status
      if (!name || !email) {
        return { name, email, status, notes, rowStatus: 'error' as const, errorReason: !name ? 'Missing name' : 'Missing email', originalIndex: i };
      }
      if (status && !cmStatuses.includes(status)) {
        return { name, email, status, notes, rowStatus: 'warning' as const, originalIndex: i };
      }
      return { name, email, status, notes, rowStatus: 'valid' as const, originalIndex: i };
    });

    setValidatedRows(rows);
    setStep(3);
  }, [parsed, mapping, defaultStatus, cmStatuses]);

  // ── Step 3: Counts ────────────────────────────────────────────────────
  const validCount = validatedRows.filter(r => r.rowStatus === 'valid').length;
  const warningCount = validatedRows.filter(r => r.rowStatus === 'warning').length;
  const errorCount = validatedRows.filter(r => r.rowStatus === 'error').length;
  const uploadableCount = validCount + warningCount;

  // ── Step 4: Upload — client-driven sequential loop ───────────────────
  const startUpload = useCallback(async () => {
    const toUpload = validatedRows.filter(r => r.rowStatus !== 'error');
    const results: UploadResult[] = toUpload.map((_, i) => ({ index: i, status: 'pending' as const }));
    setUploadResults(results);
    setStep(4);
    setUploading(true);
    cancelRef.current = false;

    for (let i = 0; i < toUpload.length; i++) {
      if (cancelRef.current) break;

      const row = toUpload[i];
      // Mark current row as uploading
      setUploadResults(prev => prev.map((r, j) => j === i ? { ...r, status: 'uploading' } : r));

      let success = false;
      let errorMsg: string | undefined;

      try {
        await addCampaignMember(apiFetch, campaignId, {
          full_name: row.name,
          work_email: row.email,
          status: row.status || 'Invited',
          ...(row.notes ? { notes: row.notes } : {}),
        });
        success = true;
      } catch (err) {
        // Check for 429 rate limit — retry once after backoff
        if (isRateLimited(err)) {
          await delay(RETRY_PAUSE_MS);
          if (cancelRef.current) break;
          try {
            await addCampaignMember(apiFetch, campaignId, {
              full_name: row.name,
              work_email: row.email,
              status: row.status || 'Invited',
              ...(row.notes ? { notes: row.notes } : {}),
            });
            success = true;
          } catch (retryErr) {
            errorMsg = retryErr instanceof Error ? retryErr.message : 'Retry failed';
          }
        } else {
          errorMsg = err instanceof Error ? err.message : 'Request failed';
        }
      }

      setUploadResults(prev => prev.map((r, j) =>
        j === i ? { ...r, status: success ? 'success' : 'error', error: errorMsg } : r,
      ));

      // Throttle between requests (skip delay after last row)
      if (i < toUpload.length - 1 && !cancelRef.current) {
        await delay(THROTTLE_MS);
      }
    }

    setUploading(false);
  }, [validatedRows, apiFetch, campaignId]);

  const handleCancel = useCallback(() => {
    cancelRef.current = true;
  }, []);

  const uploadSuccessCount = uploadResults.filter(r => r.status === 'success').length;
  const uploadErrorCount = uploadResults.filter(r => r.status === 'error').length;
  const uploadDoneCount = uploadSuccessCount + uploadErrorCount;

  // ── Step titles ───────────────────────────────────────────────────────
  const stepTitle = useMemo(() => {
    switch (step) {
      case 1: return 'Bulk Upload CSV';
      case 2: return 'Map Columns';
      case 3: return 'Review & Validate';
      case 4: return uploading ? 'Uploading...' : 'Upload Complete';
    }
  }, [step, uploading]);

  return (
    <Modal
      open={open}
      title={stepTitle}
      onClose={handleClose}
      onRequestClose={handleRequestClose}
      containerClassName="md:!max-w-2xl"
    >
      {step === 1 && <StepFileDrop fileError={fileError} onDrop={handleDrop} onFile={handleFile} fileInputRef={fileInputRef} />}
      {step === 2 && parsed && (
        <StepColumnMapping
          parsed={parsed}
          mapping={mapping}
          setMapping={setMapping}
          defaultStatus={defaultStatus}
          setDefaultStatus={setDefaultStatus}
          cmStatuses={cmStatuses}
          canProceed={!!canProceedToStep3}
          onBack={() => { setStep(1); setParsed(null); }}
          onNext={buildValidatedRows}
        />
      )}
      {step === 3 && (
        <StepValidation
          rows={validatedRows}
          setRows={setValidatedRows}
          cmStatuses={cmStatuses}
          validCount={validCount}
          warningCount={warningCount}
          errorCount={errorCount}
          uploadableCount={uploadableCount}
          onBack={() => setStep(2)}
          onRefreshStatuses={onRefreshStatuses}
          onStart={() => { void startUpload(); }}
        />
      )}
      {step === 4 && (
        <StepProgress
          results={uploadResults}
          rows={validatedRows.filter(r => r.rowStatus !== 'error')}
          uploading={uploading}
          uploadDoneCount={uploadDoneCount}
          uploadSuccessCount={uploadSuccessCount}
          uploadErrorCount={uploadErrorCount}
          onCancel={() => { handleCancel(); }}
          onDone={() => { onComplete(); handleClose(); }}
        />
      )}
    </Modal>
  );
}

// ── Step 1: File Drop ─────────────────────────────────────────────────────────

function StepFileDrop({ fileError, onDrop, onFile, fileInputRef }: {
  fileError: string;
  onDrop: (e: React.DragEvent) => void;
  onFile: (f: File) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
}) {
  return (
    <div className="space-y-4">
      <div
        className="border-2 border-dashed border-nightshade-300 dark:border-nightshade-600 rounded-xl p-8 text-center cursor-pointer hover:border-blueberry-400 dark:hover:border-blueberry-400 transition-colors"
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="w-8 h-8 mx-auto mb-3 text-nightshade-400" />
        <p className="text-sm text-nightshade-600 dark:text-nightshade-300">
          Drag & drop a CSV file here, or <span className="text-blueberry-500 underline">click to browse</span>
        </p>
        <p className="text-xs text-nightshade-400 mt-1">Supports .csv files from Salesforce, Google Sheets, Excel</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFile(file);
          }}
        />
      </div>
      {fileError && (
        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
          <XCircle className="w-4 h-4 shrink-0" />
          {fileError}
        </div>
      )}
    </div>
  );
}

// ── Step 2: Column Mapping ────────────────────────────────────────────────────

function StepColumnMapping({ parsed, mapping, setMapping, defaultStatus, setDefaultStatus, cmStatuses, canProceed, onBack, onNext }: {
  parsed: ParsedData;
  mapping: ColumnMapping;
  setMapping: React.Dispatch<React.SetStateAction<ColumnMapping>>;
  defaultStatus: string;
  setDefaultStatus: (s: string) => void;
  cmStatuses: string[];
  canProceed: boolean;
  onBack: () => void;
  onNext: () => void;
}) {
  const headerOptions = parsed.headers;

  return (
    <div className="space-y-4">
      {/* Mapping controls */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-nightshade-700 dark:text-nightshade-200">Column Mapping</span>
          <button
            className="text-xs text-blueberry-500 hover:underline"
            onClick={() => setMapping(m => ({
              ...m,
              mode: m.mode === 'full' ? 'split' : 'full',
              fullName: m.mode === 'split' ? m.fullName : '',
              firstName: m.mode === 'full' ? m.firstName : '',
              lastName: m.mode === 'full' ? m.lastName : '',
            }))}
          >
            {mapping.mode === 'full' ? 'Use separate first/last name' : 'Use full name'}
          </button>
        </div>

        {mapping.mode === 'full' ? (
          <MappingSelect label="Full Name *" value={mapping.fullName} options={headerOptions} onChange={(v) => setMapping(m => ({ ...m, fullName: v }))} />
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <MappingSelect label="First Name *" value={mapping.firstName} options={headerOptions} onChange={(v) => setMapping(m => ({ ...m, firstName: v }))} />
            <MappingSelect label="Last Name" value={mapping.lastName} options={headerOptions} onChange={(v) => setMapping(m => ({ ...m, lastName: v }))} />
          </div>
        )}

        <MappingSelect label="Work Email *" value={mapping.email} options={headerOptions} onChange={(v) => setMapping(m => ({ ...m, email: v }))} />

        <div className="grid grid-cols-2 gap-3">
          <MappingSelect label="Status" value={mapping.status} options={headerOptions} onChange={(v) => setMapping(m => ({ ...m, status: v }))} allowEmpty />
          {!mapping.status && (
            <div>
              <label className={labelCls}>Default Status</label>
              <select value={defaultStatus} onChange={(e) => setDefaultStatus(e.target.value)} className={inputCls}>
                {cmStatuses.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
        </div>
        <MappingSelect label="Notes (optional)" value={mapping.notes} options={headerOptions} onChange={(v) => setMapping(m => ({ ...m, notes: v }))} allowEmpty />
      </div>

      {/* Preview table */}
      <div>
        <p className="text-xs text-nightshade-500 dark:text-nightshade-400 mb-2">{parsed.rows.length} rows detected</p>
        <div className="max-h-48 overflow-auto border border-nightshade-200 dark:border-nightshade-700 rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-nightshade-50 dark:bg-nightshade-800 sticky top-0">
              <tr>
                {parsed.headers.map((h) => (
                  <th key={h} className="px-2 py-1.5 text-left font-medium text-nightshade-600 dark:text-nightshade-300 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-nightshade-100 dark:divide-nightshade-800">
              {parsed.rows.slice(0, 10).map((row) => (
                <tr key={row.join('|')}>
                  {parsed.headers.map((h, ci) => (
                    <td key={h} className="px-2 py-1 text-nightshade-700 dark:text-nightshade-300 whitespace-nowrap max-w-[200px] truncate">{row[ci] || ''}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {parsed.rows.length > 10 && (
            <p className="text-xs text-nightshade-400 text-center py-1">...and {parsed.rows.length - 10} more rows</p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 justify-end">
        <button onClick={onBack} className={btnSecondary}>Back</button>
        <button onClick={onNext} disabled={!canProceed} className={btnPrimary}>Next: Review</button>
      </div>
    </div>
  );
}

function MappingSelect({ label, value, options, onChange, allowEmpty }: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
  allowEmpty?: boolean;
}) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        <option value="">{allowEmpty ? '(none)' : 'Select column...'}</option>
        {options.map(h => <option key={h} value={h}>{h}</option>)}
      </select>
    </div>
  );
}

// ── Step 3: Validation Review ─────────────────────────────────────────────────

function StepValidation({ rows, setRows, cmStatuses, validCount, warningCount, errorCount, uploadableCount, onBack, onRefreshStatuses, onStart }: {
  rows: ValidatedRow[];
  setRows: React.Dispatch<React.SetStateAction<ValidatedRow[]>>;
  cmStatuses: string[];
  validCount: number;
  warningCount: number;
  errorCount: number;
  uploadableCount: number;
  onBack: () => void;
  onRefreshStatuses: () => void;
  onStart: () => void;
}) {
  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex items-center justify-between">
        <div className="flex gap-4 text-sm">
          <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
            <CheckCircle className="w-4 h-4" /> {validCount} ready
          </span>
          {warningCount > 0 && (
            <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="w-4 h-4" /> {warningCount} warnings
            </span>
          )}
          {errorCount > 0 && (
            <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
              <XCircle className="w-4 h-4" /> {errorCount} will be skipped
            </span>
          )}
        </div>
        <button
          onClick={onRefreshStatuses}
          className="flex items-center gap-1 text-xs text-nightshade-500 hover:text-blueberry-500 transition-colors"
          title="Refresh available statuses"
        >
          <RefreshCw className="w-3 h-3" /> Refresh statuses
        </button>
      </div>

      {/* Table */}
      <div className="max-h-64 overflow-auto border border-nightshade-200 dark:border-nightshade-700 rounded-lg">
        <table className="w-full text-xs">
          <thead className="bg-nightshade-50 dark:bg-nightshade-800 sticky top-0">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium text-nightshade-600 dark:text-nightshade-300">Name</th>
              <th className="px-2 py-1.5 text-left font-medium text-nightshade-600 dark:text-nightshade-300">Email</th>
              <th className="px-2 py-1.5 text-left font-medium text-nightshade-600 dark:text-nightshade-300">Status</th>
              <th className="px-2 py-1.5 text-left font-medium text-nightshade-600 dark:text-nightshade-300 w-8"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-nightshade-100 dark:divide-nightshade-800">
            {rows.map((row, i) => (
              <tr key={`${row.email}-${row.originalIndex}`} className={
                row.rowStatus === 'error' ? 'bg-red-50 dark:bg-red-900/20' :
                row.rowStatus === 'warning' ? 'bg-amber-50 dark:bg-amber-900/20' : ''
              }>
                <td className="px-2 py-1 text-nightshade-700 dark:text-nightshade-300">{row.name || <span className="text-red-500 italic">missing</span>}</td>
                <td className="px-2 py-1 text-nightshade-700 dark:text-nightshade-300">{row.email || <span className="text-red-500 italic">missing</span>}</td>
                <td className="px-2 py-1">
                  {row.rowStatus !== 'error' ? (
                    <select
                      value={row.status}
                      onChange={(e) => {
                        setRows(prev => prev.map((r, j) => j === i ? {
                          ...r,
                          status: e.target.value,
                          rowStatus: cmStatuses.includes(e.target.value) ? 'valid' : 'warning',
                        } : r));
                      }}
                      className={`text-xs bg-white dark:bg-nightshade-800 border rounded px-1.5 py-0.5 text-nightshade-700 dark:text-nightshade-300 ${
                        row.rowStatus === 'warning' ? 'border-amber-300 dark:border-amber-600' : 'border-nightshade-200 dark:border-nightshade-600'
                      }`}
                    >
                      {row.rowStatus === 'warning' && <option value={row.status}>{row.status} (unknown)</option>}
                      {cmStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <span className="text-nightshade-700 dark:text-nightshade-300">{row.status}</span>
                  )}
                </td>
                <td className="px-2 py-1 text-center">
                  {row.rowStatus === 'valid' && <CheckCircle className="w-3.5 h-3.5 text-green-500 inline" />}
                  {row.rowStatus === 'warning' && <AlertTriangle className="w-3.5 h-3.5 text-amber-500 inline" />}
                  {row.rowStatus === 'error' && <XCircle className="w-3.5 h-3.5 text-red-500 inline" />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Actions */}
      <div className="flex gap-2 justify-end">
        <button onClick={onBack} className={btnSecondary}>Back</button>
        <button onClick={onStart} disabled={uploadableCount === 0} className={btnPrimary}>
          Start Upload ({uploadableCount} {uploadableCount === 1 ? 'member' : 'members'})
        </button>
      </div>
    </div>
  );
}

// ── Step 4: Progress ──────────────────────────────────────────────────────────

function StepProgress({ results, rows, uploading, uploadDoneCount, uploadSuccessCount, uploadErrorCount, onCancel, onDone }: {
  results: UploadResult[];
  rows: ValidatedRow[];
  uploading: boolean;
  uploadDoneCount: number;
  uploadSuccessCount: number;
  uploadErrorCount: number;
  onCancel: () => void;
  onDone: () => void;
}) {
  const total = results.length;
  const pct = total > 0 ? Math.round((uploadDoneCount / total) * 100) : 0;

  return (
    <div className="space-y-4">
      {/* Progress bar */}
      <div>
        <div className="flex justify-between text-sm text-nightshade-600 dark:text-nightshade-300 mb-1">
          <span>{uploading ? `Uploading ${uploadDoneCount + 1} of ${total}...` : `Done! ${uploadSuccessCount} sent, ${uploadErrorCount} failed.`}</span>
          <span>{pct}%</span>
        </div>
        <div className="w-full h-2 bg-nightshade-200 dark:bg-nightshade-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-blueberry-500 transition-all duration-300 rounded-full"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Row list */}
      <div className="max-h-48 overflow-auto border border-nightshade-200 dark:border-nightshade-700 rounded-lg divide-y divide-nightshade-100 dark:divide-nightshade-800">
        {results.map((result, i) => (
          <div key={`upload-${result.index}`} className="flex items-center gap-2 px-3 py-1.5 text-sm">
            {result.status === 'pending' && <div className="w-4 h-4 rounded-full border border-nightshade-300 dark:border-nightshade-600" />}
            {result.status === 'uploading' && <Loader2 className="w-4 h-4 text-blueberry-500 animate-spin" />}
            {result.status === 'success' && <CheckCircle className="w-4 h-4 text-green-500" />}
            {result.status === 'error' && <XCircle className="w-4 h-4 text-red-500" />}
            <span className="text-nightshade-700 dark:text-nightshade-300 truncate">{rows[i]?.name}</span>
            <span className="text-nightshade-400 truncate text-xs">{rows[i]?.email}</span>
            {result.error && <span className="text-red-500 text-xs ml-auto shrink-0">{result.error}</span>}
          </div>
        ))}
      </div>

      {!uploading && uploadSuccessCount > 0 && (
        <p className="text-xs text-nightshade-400 dark:text-nightshade-500">Members will appear in the table in a couple of minutes.</p>
      )}

      {/* Actions */}
      <div className="flex gap-2 justify-end">
        {uploading ? (
          <button onClick={onCancel} className={`${btnSecondary} !border-red-300 !text-red-600 dark:!border-red-700 dark:!text-red-400`}>
            Cancel
          </button>
        ) : (
          <button onClick={onDone} className={btnPrimary}>Close</button>
        )}
      </div>
    </div>
  );
}

